import React from "react";

/**
 * DashboardCustomizationPanel — sidebar/drawer for the Universal Dashboard.
 * • Toggle widgets on/off via checkbox
 * • Reorder via ▲ / ▼ buttons or native HTML5 drag-and-drop
 * • "Restablecer" button clears the user layout
 *
 * Props:
 *   layout: [{ widget_id, position, enabled }]
 *   catalog: [{ id, title, category, ... }]
 *   onChange(nextLayout)
 *   onReset(): resets to backend default (all visible widgets, registry order)
 */
export default function DashboardCustomizationPanel({
  open,
  onClose,
  layout,
  catalog,
  onChange,
  onReset,
}) {
  const [dragIndex, setDragIndex] = React.useState(null);

  const catalogById = React.useMemo(() => {
    const map = {};
    for (const w of catalog) map[w.id] = w;
    return map;
  }, [catalog]);

  if (!open) return null;

  const setEntry = (i, next) => {
    const copy = layout.map((e, idx) => (idx === i ? { ...e, ...next } : e));
    onChange(copy);
  };

  const move = (from, to) => {
    if (from === to || to < 0 || to >= layout.length) return;
    const copy = layout.slice();
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    onChange(copy.map((e, i) => ({ ...e, position: i })));
  };

  const enabledCount = layout.filter((e) => e.enabled).length;

  return (
    <div
      className="fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm flex justify-end"
      onClick={onClose}
      data-testid="dashboard-customization-panel"
    >
      <aside
        className="bg-white w-full sm:max-w-md h-full overflow-auto border-l border-black flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[var(--border)] flex items-start justify-between gap-3">
          <div>
            <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
              PERSONALIZAR
            </div>
            <div className="font-display font-black text-xl tracking-tight">
              Widgets del Tablero
            </div>
            <div className="text-xs text-[var(--text-secondary)] mt-1">
              {enabledCount} de {layout.length} activos · arrastra o usa ▲▼
              para reordenar.
            </div>
          </div>
          <button
            data-testid="dashboard-customization-close"
            onClick={onClose}
            className="font-mono-tech text-sm border border-[var(--border)] w-9 h-9 hover:bg-black hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <ul className="flex-1 divide-y divide-[var(--border)]">
          {layout.map((entry, i) => {
            const def = catalogById[entry.widget_id];
            if (!def) return null;
            return (
              <li
                key={entry.widget_id}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIndex !== null && dragIndex !== i) move(dragIndex, i);
                  setDragIndex(null);
                }}
                data-testid={`dashboard-customization-item-${entry.widget_id}`}
                className={`p-4 flex items-center gap-3 ${
                  dragIndex === i ? "opacity-60 bg-[var(--bg)]" : ""
                }`}
              >
                <span
                  className="font-mono-tech text-lg cursor-grab select-none text-[var(--text-secondary)]"
                  aria-hidden
                >
                  ⋮⋮
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">
                    {def.title}
                  </div>
                  <div className="font-mono-tech text-[0.6rem] tracking-wider text-[var(--text-secondary)] uppercase">
                    {def.category} · {def.type}
                  </div>
                </div>
                <button
                  onClick={() => move(i, i - 1)}
                  disabled={i === 0}
                  aria-label="Subir"
                  className="font-mono-tech text-xs border border-[var(--border)] w-7 h-7 hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  ▲
                </button>
                <button
                  onClick={() => move(i, i + 1)}
                  disabled={i === layout.length - 1}
                  aria-label="Bajar"
                  className="font-mono-tech text-xs border border-[var(--border)] w-7 h-7 hover:bg-black hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  ▼
                </button>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid={`dashboard-customization-toggle-${entry.widget_id}`}
                    type="checkbox"
                    checked={entry.enabled}
                    onChange={(e) =>
                      setEntry(i, { enabled: e.target.checked })
                    }
                    className="w-4 h-4 accent-black"
                  />
                </label>
              </li>
            );
          })}
        </ul>

        <div className="p-4 border-t border-[var(--border)] flex items-center justify-between gap-2">
          <button
            data-testid="dashboard-customization-reset"
            onClick={onReset}
            className="font-display font-bold text-sm border border-[var(--border)] px-4 py-2 hover:bg-black hover:text-white transition-colors"
          >
            Restablecer
          </button>
          <button
            onClick={onClose}
            className="font-display font-bold text-sm bg-black text-white px-4 py-2 hover:bg-[#0E5FFF] transition-colors"
          >
            Listo
          </button>
        </div>
      </aside>
    </div>
  );
}
